import cloneDeep from 'lodash/cloneDeep';
import forEach from 'lodash/forEach';
import isUndefined from 'lodash/isUndefined';
import map from 'lodash/map';
import some from 'lodash/some';
import includes from 'lodash/includes';
import sortBy from 'lodash/sortBy';
import moment from 'moment';
import APIUtils from '@spothero/utils/api';
import SHGlobals from 'sh-globals';
import FacilityAPI from 'facility-api';

const SpotUtils = {
    formatAccessHours(accessHours) {
        const accessHoursFormatted = [];

        if (!accessHours) {
            return accessHoursFormatted;
        }

        let alwaysOpen = false;

        // if accessHours contain text and periods, evaluate text
        // and update accessHours to reflect accessHours.periods
        if (accessHours.text) {
            if (accessHours.text.length && accessHours.text[0] === 'This facility is open 24/7.') {
                alwaysOpen = true;
            }

            accessHours = accessHours.periods;
        }

        if (!alwaysOpen && accessHours.length) {
            const {
                start_dow_int,
                end_dow_int,
                start_time,
                end_time
            } = accessHours[0];

            if (start_dow_int === end_dow_int && start_time === end_time) {
                alwaysOpen = true;
            }
        }

        if (alwaysOpen) {
            accessHoursFormatted.push({
                type: '247'
            });

            return accessHoursFormatted;
        }

        forEach(accessHours, period => {
            let days = period.start_dow;
            let value;

            if (period.start_dow !== period.end_dow) {
                days = `${period.start_dow} - ${period.end_dow}`;
            }

            if (period.start_time && period.end_time) {
                const inFormat = 'YYYY-MM-DD HH:mm:ss';
                const outFormat = 'h:mma';

                value = `${moment(`2025-01-01 ${period.start_time}`, inFormat).format(outFormat)} - ${moment(`2025-01-01 ${period.end_time}`, inFormat).format(outFormat)}`;
            } else if (period.hours_type) {
                value = period.hours_type;
            }

            accessHoursFormatted.push({
                days: days,
                value: value,
                type: period.hours_type
            });
        });

        return accessHoursFormatted;
    },

    buildCheckoutURL(spot, rate, searchParams) {
        let spotUrl = spot.spot_url || spot.facility.facility_url;

        if (!window.SHGlobals.isDeployed) {
            spotUrl = `${window.location.origin}/${spotUrl.split('/').slice(3).join('/')}`;
        }

        if (searchParams.monthly) {
            spotUrl += `?rid=${rate.rule_id}&starts=${searchParams.monthly_start_date}&monthly=true`;
        } else {
            if (!isUndefined(searchParams.eid)) {
                spotUrl = (!includes(rate.url, 'eid'))
                    ? `${rate.url}&eid=${searchParams.eid}`
                    : rate.url;
            } else {
                spotUrl += `?rid=${rate.rule_group_id}&starts=${searchParams.starts}&ends=${searchParams.ends}`;
            }
        }

        if (searchParams.rebook_reservation_id) {
            if (includes(spotUrl, '?')) {
                spotUrl += `&rebook_reservation_id=${searchParams.rebook_reservation_id}`;
            } else {
                spotUrl += `?rebook_reservation_id=${searchParams.rebook_reservation_id}`;
            }
        }

        return spotUrl;
    },

    prepare(result, searchParams) {
        const facility = (result.facility && result.facility.id) ? result.facility : result;
        const preparedResult = {
            ...result,
            spotId: facility.parking_spot_id || facility.id,
            hourly_rates: result.hourly_rates || [],
            monthly_rates: result.monthly_rates || [],
            activeMarker: false,
            isFavorite: false,
            hasTravelDistanceUpdated: false,
            hidden: false,
            selectedRate: null,
            url: result.spot_url,
            hasOversizedFee: (facility.vehicle_size && facility.vehicle_size === 'oversized_with_fee') || false,
            oversizedFeeMessage: facility.oversize_description || null,
            vehicleRestriction: facility.vehicle_restriction_description || null,
            facility: {
                ...facility,
                access_hours_formatted: SpotUtils.formatAccessHours(facility.hours_of_operation)
            }
        };
        let priceArr = [];
        let currentRate;

        // prepare hourly rates
        if (preparedResult.hourly_rates) {
            forEach(preparedResult.hourly_rates, (rate, index) => {
                currentRate = preparedResult.hourly_rates[index];

                if (isUndefined(currentRate.hidden)) {
                    currentRate.hidden = false;
                }

                // is this a recently updated monthly rate? - BK 4/1/2015
                currentRate.newMonthlyRate = false;

                // url
                currentRate.fullUrl = SpotUtils.buildCheckoutURL(result, currentRate, searchParams);

                // starts & ends: remove timezone info to conform datetimes to `YYYY-MM-DDTHH:MM` format
                currentRate.starts = moment.tz(
                    currentRate.starts,
                    window.currentCityTimezone
                ).format(SHGlobals.SERVER_DATE_TIME_FORMAT);

                currentRate.ends = moment.tz(
                    currentRate.ends,
                    window.currentCityTimezone
                ).format(SHGlobals.SERVER_DATE_TIME_FORMAT);

                // price
                priceArr = (currentRate.price / 100).toFixed(2).toString('0.00').replace(/\.0+$/, '').split('.');
                currentRate.price_formatted = `$${priceArr[0]}`;

                if (priceArr[1]) {
                    currentRate.price_formatted += `<sup>.${priceArr[1]}</sup>`;
                }

                // amenities
                currentRate.amenities = currentRate.amenities;

                // access hours
                if (preparedResult.facility.access_hours_formatted && preparedResult.facility.access_hours_formatted.length) {
                    currentRate.access_hours_formatted = preparedResult.facility.access_hours_formatted;
                } else {
                    currentRate.access_hours_formatted = false;
                }

                // full rule id
                currentRate.fullRule = currentRate.rule_group_id;
            });
        }

        // prepare monthly rates
        if (preparedResult.monthly_rates) {
            forEach(preparedResult.monthly_rates, (rate, index) => {
                currentRate = preparedResult.monthly_rates[index];

                if (isUndefined(currentRate.hidden)) {
                    currentRate.hidden = false;
                }

                // is this a recently updated monthly rate? - BK 4/1/2015
                if (!isUndefined(currentRate.redemption_instructions) && currentRate.redemption_instructions.length) {
                    currentRate.newMonthlyRate = true;
                } else {
                    currentRate.newMonthlyRate = false;
                }

                // url
                currentRate.fullUrl = SpotUtils.buildCheckoutURL(result, currentRate, searchParams);

                // price
                priceArr = (currentRate.price / 100).toFixed(2).toString('0.00').replace(/\.0+$/, '').split('.');
                currentRate.price_formatted = `$${priceArr[0]}`;

                if (priceArr[1]) {
                    currentRate.price_formatted += `<sup>.${priceArr[1]}</sup>`;
                }

                // amenities
                // only update if result contains facility otherwise we assume its already present
                // TODO this is a fallback for monthly rates that have not yet been updated at the rate level
                if (!currentRate.newMonthlyRate) {
                    currentRate.amenities = facility.amenities_full;
                } else {
                    currentRate.amenities = currentRate.amenities_full;
                }

                // access hours
                if (currentRate.access_hours.length) {
                    currentRate.access_hours_formatted = SpotUtils.formatAccessHours(currentRate.access_hours);
                } else {
                    if (facility.hours_of_operation.periods.length) {
                        currentRate.access_hours_formatted = preparedResult.facility.access_hours_formatted;
                    } else {
                        currentRate.access_hours_formatted = false;
                    }
                }

                // Change fee fields from pennies to dollars
                currentRate.introductory.after_period_price_amount = currentRate.introductory.after_period_price_amount / 100;
                currentRate.contract.termination_fee = currentRate.contract.termination_fee / 100;
                currentRate.in_out.fee = currentRate.in_out.fee / 100;

                // full rule id
                currentRate.fullRule = currentRate.rule_id;
            });
        }

        // set selectedRate
        if (isUndefined(preparedResult.selectedRate) || !preparedResult.selectedRate) {
            preparedResult.selectedRate = null;

            if (searchParams && searchParams.monthly) {
                if (preparedResult.monthly_rates && preparedResult.monthly_rates.length) {
                    preparedResult.selectedRate = cloneDeep(preparedResult.monthly_rates[0]);
                } else {
                    preparedResult.selectedRate = null;
                }
            } else {
                if (preparedResult.hourly_rates && preparedResult.hourly_rates.length) {
                    preparedResult.selectedRate = cloneDeep(preparedResult.hourly_rates[0]);
                } else {
                    preparedResult.selectedRate = null;
                }
            }
        }

        return preparedResult;
    },

    filter(spots, filterState, isMonthly) {
        const {
            activeReservationType,
            activeAmenities,
            price,
            reservationTypes
        } = filterState;
        const relatedReservationTypes = reservationTypes[activeReservationType].related;
        const newFiltered = [];

        forEach(spots, spot => {
            const rateType = (isMonthly) ? 'monthly_rates' : 'hourly_rates';
            const rates = spot[rateType];
            let show = true;

            if (price && spot.selectedRate.price > price) {
                show = false;
            }

            // default rates to hidden = false
            forEach(rates, rate => {
                rate.hidden = false;
            });

            // filter monthly results by reservationType
            if (show && isMonthly && activeReservationType !== 'all') {
                let hasMatchingRate = false;

                forEach(rates, rate => {
                    // set selectedRate if type matches
                    // if not set as hidden (from filters)
                    if (!hasMatchingRate && rate.reservation_type === activeReservationType) {
                        spot.selectedRate = rate;
                        hasMatchingRate = true;
                    }

                    // attempt to match related rates if neccesary
                    if (!hasMatchingRate) {
                        forEach(relatedReservationTypes, relatedReservationType => {
                            if (rate.reservation_type === relatedReservationType) {
                                rate.hidden = false;
                                spot.selectedRate = rate;
                                hasMatchingRate = true;
                            }
                        });
                    }
                });

                show = hasMatchingRate;
            }

            if (show && activeAmenities.length) {
                let hasMatchingRate = false;

                forEach(rates, rate => {
                    // filter by active amenities
                    let rateMatches = [];

                    forEach(activeAmenities, amenity => {

                        if (includes(amenity, 'no-')) {
                            if (!some(rate.amenities, {slug: amenity.replace('no-', '')})) {
                                rateMatches.push(amenity);
                            }
                        } else {
                            if (some(rate.amenities, {slug: amenity})) {
                                rateMatches.push(amenity);
                            }
                        }
                    });

                    if (rateMatches.length === activeAmenities.length) {
                        hasMatchingRate = true;
                    } else {
                        rate.hidden = true;
                    }
                });

                show = hasMatchingRate;
            }

            spot[rateType] = rates;

            if (show) {
                spot.hidden = false;
                newFiltered.push(spot);
            } else {
                spot.hidden = true;
            }
        });

        return newFiltered;
    },

    sortByAttribute(spots, attr = 'distance') {
        let comparator = attr;

        if (attr === 'price') {
            comparator = spot => {
                return spot.selectedRate.price;
            };
        }

        if (attr === 'recommended') {
            comparator = spot => {
                return spot.facility.order;
            };
        }

        if (attr === 'alpha') {
            comparator = spot => {
                return spot.facility.title;
            };
        }

        if (attr === 'shuttle-time') {
            comparator = spot => {
                return spot.facility.airport.shuttle.duration;
            };
        }

        return sortBy(spots, [comparator]);
    },

    prepareGroup(spots, searchParams) {
        return map(spots, spot => {
            return SpotUtils.prepare(spot, searchParams);
        });
    },

    loadRates({
        spotId,
        starts,
        ends,
        isMonthly,
        searchDataParams,
        spot = null,
        eventId = null,
        onSuccessWithRates,
        onSuccessWithoutRates,
        onError
    }) {
        const rateLoader = APIUtils.makeCancelable(
            FacilityAPI.getRates({
                spotId,
                starts,
                ends,
                eventId,
                includeFacility: true
            })
        );

        rateLoader
            .promise
            .then(resp => {
                const spotData = resp.data.data;
                const rateType = (isMonthly) ? 'monthly_rates' : 'hourly_rates';

                if (spotData[rateType].length > 0) {
                    // TODO: BK - is there another way to better handle this?
                    // need to assign updated starts and ends to params on state in this case because
                    // updateSearchTimes is not called until the callback to first ensure time changes are valid
                    const newSpotData = SpotUtils.prepare(
                        spotData,
                        {
                            ...searchDataParams,
                            starts,
                            ends
                        }
                    );
                    // MP: When loading rates from a `sid` on page load, no spot data will be provided to this method so
                    // we just want to use the one that comes back from the response so that we have spot data to show
                    // the spot details properly on the search page
                    const savedSpotData = (spot) ? spot : newSpotData;
                    let newRate = newSpotData.selectedRate;

                    if (spotData[rateType].length > 1 && spot && spot.selectedRate) {
                        // if there are multiple rates make sure to select the same rate that was selected pre-reloading
                        // so that the UI matches up correctly and the proper rate is selected in the choices
                        const priorMatchedRate = find(newSpotData[rateType], {fullRule: spot.selectedRate.fullRule});

                        if (priorMatchedRate) {
                            newRate = priorMatchedRate;
                        }
                    }

                    if (onSuccessWithRates) {
                        onSuccessWithRates({
                            ...savedSpotData,
                            hourly_rates: newSpotData.hourly_rates,
                            monthly_rates: newSpotData.monthly_rates,
                            lowest_monthly_price: newSpotData.lowest_monthly_price,
                            selectedRate: newRate
                        });
                    }
                } else {
                    if (onSuccessWithoutRates) {
                        onSuccessWithoutRates();
                    }
                }
            })
            .catch(error => {
                if (onError) {
                    onError(error);
                }
            });

        return rateLoader;
    }
};

export default SpotUtils;



// WEBPACK FOOTER //
// ./spothero/static/main/js/utils/spot.js